import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import {
  ArrowUpRightSolidV6,
  CheckSolidV6,
  GearSolidV6,
  PhonePlusSolidV6,
  TriangleExclamationSolidV6,
} from "@shared/v2/Icomoon";
import Toast from "@shared/Toast";

import { Section, SectionLabel } from "../../Reports/components/BusinessDashboard";
import Tooltip from "../../shared/v2/Tooltip";
import CircleInfoSolidV6 from "../../shared/v2/Icomoon/Icons/CircleInfoSolidV6";
import AddBrivityPhone from "./AddBrivityPhone";
import AgentRow from "./AgentRow";
import { checkForRoutingNumber, createCnam, deleteCnam, updateCnam } from "./services";
import Error from "../../Reports/components/DashboardShared/Error";
import TeamCallRecordingSettingsModal from "./TeamCallRecordingSettingsModal";
import { setCnamToAgent, useAgentsWithPhoneUsage } from "../../reducers/layoutReducer/phoneSettingsReducer";
import {
  useAccountCallRecordingSettings,
  useCurrentUser,
  useFeatureFlags,
} from "../../reducers/layoutReducer/selectors";
import EditAddCNAMModal from "./EditAddCNAMModal";
import DeleteCNAMModal from "./DeleteCNAMModal";
import useActiveProducts from "../../Products/hooks/useActiveProducts";
import { ProductFamilyIds } from "../../Products/constants";

const getInitials = (name) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .filter(Boolean)
    .slice(0, 1)
    .concat(name.trim().split(/\s+/).slice(-1)[0][0].toUpperCase())
    .join(".");

const TOAST_TEXT_INIT = "Caller ID Name added! Please allow up to 72 hours for change to take effect.";

const ERR_TEXT =
  "There was an issue saving your Caller ID Name. Please try again later. If the issue persists, please reach out to support.";

const PhoneSettings = () => {
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();
  const callRecordingAdjustmentsEnabled = featureFlags?.callRecordingAdjustments;
  const agentsWithPhoneUsage = useAgentsWithPhoneUsage();
  const accountCallRecordingSettings = useAccountCallRecordingSettings();
  const [agentToAddNumber, setAgentToAddNumber] = useState(null);
  const [error, setError] = useState("");
  const [userAddEditCnam, setUserAddEditCnam] = useState(null);
  const [deleteCnamModalOpen, setIsDeleteCnamModalOpen] = useState(null);
  const [errorAddEditCnam, setErrorAddEditCnam] = useState(null);
  const [toastObj, setShowToastObj] = useState({
    text: TOAST_TEXT_INIT,
    show: false,
    error: false,
  });
  const [isCallRecordingModalOpen, setIsCallRecordingModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { activeProducts, loading } = useActiveProducts(ProductFamilyIds.PhoneSystem);
  const hasPhoneSystem = activeProducts.length > 0;
  const isBrivity = !currentUser.account.isPlace && !currentUser.account.isLever;

  useEffect(() => {
    if (toastObj.show) {
      setTimeout(() => {
        setShowToastObj((prev) => ({ ...prev, show: false }));
      }, 5000);
    }
  }, [toastObj.show]);

  const onManageNumberModalClosed = () => {
    setAgentToAddNumber(null);
  };

  const onNumberSaved = () => {
    setAgentToAddNumber(null);

    window.location.reload();
  };

  const addBrivityNumber = async (agent) => {
    const { error: errorOnAdd } = await checkForRoutingNumber(agent.id);

    if (errorOnAdd) {
      setError(errorOnAdd);
    } else {
      setAgentToAddNumber(agent);
    }
  };

  const createCnamHandler = async ({ displayName, userId }) => {
    const { res, err } = await createCnam({ displayName, userId });

    if (err) {
      setErrorAddEditCnam(true);
      return { err: ERR_TEXT };
    }

    dispatch(
      setCnamToAgent({
        userId: res.data.userId,
        twilioCnam: res.data,
      }),
    );
    return { err: null };
  };

  const updateCnamHandler = async ({ displayName, id }) => {
    const { res, err } = await updateCnam({ displayName, id });

    if (err) {
      setErrorAddEditCnam(true);
      return { err: ERR_TEXT };
    }

    dispatch(
      setCnamToAgent({
        userId: res.data.userId,
        twilioCnam: res.data,
      }),
    );

    return { err: null };
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-stretch tw-flex-wrap tw-gap-8px tw-bg-tinted-gray-50 tw-p-16px">
      <Toast
        text={toastObj.text}
        containerClassName={`${toastObj.show ? "tw-mt-[-100px] tw-opacity-100" : "tw-mt-[-80px] tw-opacity-0"} tw-p-12px tw-z-[9991] tw-transform tw--translate-x-1/2 tw-transition-all tw-duration-500

        ${toastObj.error ? "tw-bg-[#FAE6E6] tw-border-[#FAC5C3]" : ""}`}
        iconClassName={toastObj.error ? "tw-text-[#E5342E]" : "tw-text-[#36B25F]"}
        Icon={toastObj.error ? TriangleExclamationSolidV6 : CheckSolidV6}
      />
      <Section className="tw-p-[32px] tw-gap-8px">
        {error && (
          <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "center" })}>
            <Error message={error} show={Boolean(error)} onClose={() => setError("")} />
          </div>
        )}
        {!loading && isBrivity && !hasPhoneSystem && (
          <Alert
            icon={<PhonePlusSolidV6 size="l" />}
            variant={isBrivity ? "brivity" : "info"}
            title="You can now add team, office, or campaign lines."
            text="Never miss a call for sign call tracking, office lines, marketing campaigns, or team queues. Leads still flow straight into your CRM."
            containerClass="tw-mb-[16px]"
            customAction={
              <Button
                className="tw-inline-flex tw-justify-center tw-items-center tw-gap-[6px]"
                size="medium"
                schema="secondary"
                onClick={() => window.location.assign("/phone_system")}
              >
                Get Phone System <ArrowUpRightSolidV6 />
              </Button>
            }
          />
        )}
        <div className="tw-pb-[24px] tw-flex tw-justify-between">
          <span className="tw-text-28d tw-text-gray-75">Phone Settings</span>
          <div className="tw-flex tw-gap-[8px]">
            {!loading && isBrivity && hasPhoneSystem && (
              <Button
                className="tw-inline-flex tw-justify-center tw-items-center tw-gap-[6px]"
                size="medium"
                schema="secondary"
                onClick={() => window.location.assign("/phone_system?view=settings")}
              >
                <GearSolidV6 />
                Tracking Numbers
                <ArrowUpRightSolidV6 />
              </Button>
            )}
            {featureFlags.callRecording && currentUser.account.isAdmin && (
              <Button
                className="tw-inline-flex tw-justify-center tw-items-center tw-gap-[6px]"
                size="medium"
                onClick={() => setIsCallRecordingModalOpen(true)}
              >
                <GearSolidV6 />
                {callRecordingAdjustmentsEnabled ? "AI Call Recording" : "Call Recording"}
              </Button>
            )}
          </div>
        </div>
        <SectionLabel>Brivity Numbers</SectionLabel>
        <div className="tw-mt-[16px] tw-width-auto tw-relative tw-flex tw-flex-col tw-flex-nowrap tw-overflow-auto">
          <table>
            <thead className="tw-whitespace-nowrap">
              <tr>
                <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                  <span className="tw-text-12px tw-text-gray-50 tw-font-normal">Agent Name</span>
                </th>
                <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                  <div className="tw-flex tw-gap-8px">
                    <span className="tw-text-12px tw-text-gray-50 tw-font-normal tw-flex tw-gap-[4px]">
                      Routing Number
                      <Tooltip
                        innerClassName="tw-max-w-[300px]"
                        placement="right"
                        content="Used for Brivity notifications, texting, and click-to-call"
                        trigger={<CircleInfoSolidV6 />}
                      />
                    </span>
                  </div>
                </th>
                <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                  <span className="tw-text-12px tw-text-gray-50 tw-font-normal">Brivity Number</span>
                </th>
                {featureFlags.cnamRegistration && (
                  <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                    <span className="tw-text-12px tw-text-gray-50 tw-font-normal tw-flex tw-gap-[4px]">
                      Caller ID Name
                      <Tooltip
                        innerClassName="tw-max-w-[300px]"
                        placement="right"
                        content="This is the name that shows up when you call someone from Brivity"
                        trigger={<CircleInfoSolidV6 />}
                      />
                    </span>
                  </th>
                )}
                {featureFlags.callRecording && (
                  <>
                    <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                      <span className="tw-text-12px tw-text-gray-50 tw-font-normal tw-flex tw-gap-[4px]">
                        Inbound Call Recording
                        {accountCallRecordingSettings.inboundRecording !== "by_number" && (
                          <Tooltip
                            innerClassName="tw-max-w-[300px]"
                            trigger={<CircleInfoSolidV6 />}
                            content="This has been set for all accounts. Go to Call Recording settings to update."
                          />
                        )}
                      </span>
                    </th>
                    <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                      <span className="tw-text-12px tw-text-gray-50 tw-font-normal tw-flex tw-gap-[4px]">
                        Outbound Call Recording
                        {accountCallRecordingSettings.outboundRecording !== "by_number" && (
                          <Tooltip
                            innerClassName="tw-max-w-[300px]"
                            trigger={<CircleInfoSolidV6 />}
                            content="This has been set for all accounts. Go to Call Recording settings to update."
                          />
                        )}
                      </span>
                    </th>
                    {callRecordingAdjustmentsEnabled && (
                      <th className="tw-p-[8px] tw-border-b-[1px] tw-border-solid tw-border-gray-15 tw-border-t-0 tw-border-x-0">
                        <span className="tw-text-12px tw-text-gray-50 tw-font-normal tw-flex tw-gap-[4px]">
                          AI Call Summary
                          <Tooltip
                            placement="left"
                            innerClassName="tw-max-w-[180px]"
                            multiline
                            trigger={<CircleInfoSolidV6 />}
                            content="Call Recording must be enabled to generate an AI summary"
                          />
                        </span>
                      </th>
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody className="tw-whitespace-nowrap">
              {agentsWithPhoneUsage.map((agent) => (
                <AgentRow
                  key={agent.uuid}
                  agent={agent}
                  addBrivityNumber={addBrivityNumber}
                  openAddEditCnamModalHandlr={setUserAddEditCnam}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <AddBrivityPhone
        agent={agentToAddNumber}
        onClose={onManageNumberModalClosed}
        onNumberSaved={onNumberSaved}
      />

      <TeamCallRecordingSettingsModal
        open={isCallRecordingModalOpen}
        onClose={() => setIsCallRecordingModalOpen(false)}
      />

      <EditAddCNAMModal
        showCnam={
          userAddEditCnam
            ? {
                ...userAddEditCnam,
                displayName:
                  userAddEditCnam?.action === "add" && userAddEditCnam?.displayName?.length > 15
                    ? getInitials(userAddEditCnam?.displayName)
                    : userAddEditCnam?.displayName,
              }
            : null
        }
        onClose={() => setUserAddEditCnam(null)}
        onSave={async (p) => {
          const isAddingCnam = userAddEditCnam?.action === "add";

          const handlerCallBack = isAddingCnam ? createCnamHandler : updateCnamHandler;

          const { err } = await handlerCallBack(p);

          setShowToastObj({
            text: err || TOAST_TEXT_INIT,
            show: true,
            error: Boolean(err),
          });
        }}
        error={errorAddEditCnam}
        onErrorMsgAddCnam={setErrorAddEditCnam}
        title={userAddEditCnam?.action === "add" ? "Add Caller ID Name" : "Edit Caller ID Name"}
        openDeleteCnamModalHandlr={
          userAddEditCnam?.action === "add"
            ? null
            : (cnam) => {
                setUserAddEditCnam(null);
                setIsDeleteCnamModalOpen(cnam);
              }
        }
      />
      <DeleteCNAMModal
        showCnam={deleteCnamModalOpen}
        onClose={() => setIsDeleteCnamModalOpen(null)}
        onDelete={async () => {
          const { res } = await deleteCnam({ id: deleteCnamModalOpen?.id });

          if (res) {
            dispatch(
              setCnamToAgent({
                userId: deleteCnamModalOpen?.userId,
                twilioCnam: null,
              }),
            );

            setIsDeleteCnamModalOpen(null);
          }
        }}
      />
    </div>
  );
};

export default PhoneSettings;
